import {
  Component,
  input,
  signal,
  inject,
  ChangeDetectionStrategy,
  computed,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { challenge as ChallengeGen } from '@valvur/shared-grpc';
import { formatDuration } from '../../utils/duration.util';
import { ScriptStateService } from '../../services/script-state.service';
import { LessonScriptComponent } from '../lesson-script/lesson-script.component';
import { ToastrService } from 'ngx-toastr';
import { COURSE_CREATOR_BRIDGE } from '../../../bridges/course-creator-bridge.token';
import { VIDEO_HERO_PRESETS } from '../../../../../../../shared/constants/video-hero-presets.constants';
import { ModalComponent } from '../../../../../../../shared/components/ui/modal/modal.component';
import { CourseVideoComponent } from '../../../../course-video/course-video.component';

/** Tasks from HTTP may expose media fields as camelCase or snake_case. */
function pickMediaUrl(
  task: ChallengeGen.Task,
  keys: readonly string[],
): string | undefined {
  const raw = task as unknown as Record<string, unknown>;
  for (const key of keys) {
    const v = raw[key];
    if (typeof v === 'string') {
      const s = v.trim();
      if (s) return s;
    }
  }
  return undefined;
}

/**
 * Component responsible for displaying a single lesson item
 * Follows Single Responsibility Principle - only handles individual lesson display
 */
@Component({
  selector: 'app-lesson-item',
  standalone: true,
  imports: [
    CommonModule,
    TranslatePipe,
    LessonScriptComponent,
    ModalComponent,
    CourseVideoComponent,
  ],
  templateUrl: './lesson-item.component.html',
  styleUrl: './lesson-item.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ScriptStateService],
})
export class LessonItemComponent {
  #bridge = inject(COURSE_CREATOR_BRIDGE);
  task = input.required<ChallengeGen.Task>();
  course = input<ChallengeGen.Course | undefined>(undefined);
  index = input.required<number>();
  layoutVariant = input<'school' | 'admin'>('school');

  /** Signed playback URL or raw path — same sources course detail uses. */
  videoContentUrl = computed(() =>
    pickMediaUrl(this.task(), [
      'contentUrl',
      'content_url',
      'videoUrl',
      'video_url',
    ]),
  );

  audioContentUrl = computed(() =>
    pickMediaUrl(this.task(), ['audioUrl', 'audio_url']),
  );

  /** Same preview pattern as course detail (school): modal + {@link CourseVideoComponent}. */
  contentPreviewOpen = signal(false);
  contentPreview = signal<
    { kind: 'video'; url: string; poster?: string } | { kind: 'audio'; url: string } | null
  >(null);

  private scriptStateService = inject(ScriptStateService);
  private toastr = inject(ToastrService);

  isExpanded = signal(false);
  isGeneratingAudio = signal(false);
  isGeneratingVideo = computed(() => this.task().status === 'GENERATING_VIDEO');

  /** VALVUR catalog — `presetId` for video worker (course-level hero not on `Course.settings` yet). */
  isValvurCourse = computed(() => this.course()?.source === 'VALVUR');

  /** My courses (school) only — admin catalog omits generate-audio. */
  showGenerateAudio = computed(() => this.layoutVariant() !== 'admin');

  formatDuration = formatDuration;

  openContentPreview(): void {
    const video = this.videoContentUrl();
    const audio = this.audioContentUrl();
    if (video) {
      const poster = pickMediaUrl(this.task(), [
        'thumbnailUrl',
        'thumbnail_url',
        'coverUrl',
        'cover_url',
      ]);
      this.contentPreview.set({
        kind: 'video',
        url: video,
        ...(poster ? { poster } : {}),
      });
      this.contentPreviewOpen.set(true);
      return;
    }
    if (audio) {
      this.contentPreview.set({ kind: 'audio', url: audio });
      this.contentPreviewOpen.set(true);
    }
  }

  onContentPreviewClosed(): void {
    this.contentPreviewOpen.set(false);
    this.contentPreview.set(null);
  }

  async toggleExpansion(): Promise<void> {
    const newState = !this.isExpanded();
    this.isExpanded.set(newState);

    // Fetch script when expanding if not already loaded
    if (
      newState &&
      this.task().scriptUrl &&
      !this.scriptStateService.getScript(this.task().id)
    ) {
      await this.scriptStateService.fetchScript(
        this.task().id,
        this.task().scriptUrl!
      );
    }
  }

  getScript(): string | null {
    return this.scriptStateService.getScript(this.task().id);
  }

  isScriptLoading(): boolean {
    return this.scriptStateService.isScriptLoading(this.task().id);
  }

  hasScriptError(): boolean {
    return this.scriptStateService.hasScriptError(this.task().id);
  }

  async generateVideo(): Promise<void> {
    if (!this.task().scriptUrl) {
      this.toastr.error('Script URL is required to generate video');
      return;
    }

    // Check if course is available
    const course = this.course();
    if (!course) {
      this.toastr.error('Course information is not available');
      return;
    }

    try {
      // Fetch the script first if not already loaded
      if (!this.scriptStateService.getScript(this.task().id)) {
        await this.scriptStateService.fetchScript(
          this.task().id,
          this.task().scriptUrl!
        );
      }

      // Get the script text
      const scriptText = this.scriptStateService.getScript(this.task().id);

      if (!scriptText) {
        this.toastr.error('Failed to load script for video generation');
        return;
      }

      const presetId = this.isValvurCourse()
        ? VIDEO_HERO_PRESETS[0]?.name ?? ''
        : '';

      const heroVideoSettings = course.settings as
        | {
            videoMainHeroName?: string;
            videoBackgroundHeroNames?: string[];
          }
        | undefined;
      const mainHeroName =
        heroVideoSettings?.videoMainHeroName?.trim() ||
        (this.isValvurCourse() ? VIDEO_HERO_PRESETS[0]?.name ?? '' : '');
      const backgroundHeroNames =
        heroVideoSettings?.videoBackgroundHeroNames?.filter(Boolean) ?? [];

      await this.#bridge.generateVideo({
        sessionId: '',
        presetId,
        taskId: this.task().id,
        courseId: this.task().courseId,
        script: scriptText,
        durationSeconds: 8,
        aspectRatio: '16:9',
        tone: this.mapTone(course.settings?.tone || ''),
        audience: this.mapAudience(course.settings?.audience || ''),
        isTask: this.isValvurCourse(),
        mainHeroName,
        backgroundHeroNames,
      });

      this.toastr.success('Video generation started successfully');
    } catch (error: unknown) {
      this.toastr.error('Failed to start video generation');
      console.error('Video generation error:', error);
    } finally {
      //   this.isGeneratingVideo.set(false);
    }
  }

  private mapAudience(audience: string): string {
    const audienceMap: Record<string, string> = {
      ['preschool-3-5']: 'Preschool (3-5 years',
      ['kindergarten-5-6']: 'Kindergarten (5-6 years)',
      ['early-elementary-6-8']: 'Early Elementary (6-8 years)',
      ['elementary-8-10']: 'Elementary (8-10 years)',
      ['late-elementary-10-12']: 'Late Elementary (10-12 years)',
      ['middle-school-12-14']: 'Middle School (12-14 years)',
      ['early-teens-14-16']: 'Early Teens (14-16 years)',
      ['mixed-ages']: 'Mixed Ages',
      ['beginners']: 'Beginners',
      ['intermediate-learners']: 'Intermediate Learners',
      ['advanced-kids']: 'Advanced Kids',
      ['other']: 'Other',
    };
    return audienceMap[audience] || 'Other';
  }

  async generateAudio(): Promise<void> {
    this.isGeneratingAudio.set(true);
    if (!this.task().scriptUrl) {
      this.toastr.error('Script URL is required to generate audio');
      return;
    }

    // Check if course is available
    const course = this.course();
    if (!course) {
      this.toastr.error('Course information is not available');
      return;
    }

    try {
      // Fetch the script first if not already loaded
      if (!this.scriptStateService.getScript(this.task().id)) {
        await this.scriptStateService.fetchScript(
          this.task().id,
          this.task().scriptUrl!
        );
      }

      // Get the script text
      const scriptText = this.scriptStateService.getScript(this.task().id);

      if (!scriptText) {
        this.toastr.error('Failed to load script for audio generation');
        return;
      }

      // Generate audio using the script
      await this.#bridge.generateAudio({
        courseId: this.task().courseId,
        taskId: this.task().id,
        script: scriptText,
        voice: 'default', // Male / Female voices are configurable in the future
      });
    } catch (error: unknown) {
      this.toastr.error('Failed to start audio generation');
      console.error('Audio generation error:', error);
    } finally {
      this.isGeneratingAudio.set(false);
    }
  }

  private mapTone(tone: string): string {
    const toneMap: Record<string, string> = {
      ['fun-playful']: 'Fun & Playful',
      ['friendly-warm']: 'Friendly & Warm',
      ['simple-clear']: 'Simple & Clear',
      ['encouraging-supportive']: 'Encouraging & Supportive',
      ['adventurous-exciting']: 'Adventurous & Exciting',
      ['curious-wondering']: 'Curious & Wondering',
      ['gentle-patient']: 'Gentle & Patient',
      ['enthusiastic-energetic']: 'Enthusiastic & Energetic',
      ['storytelling-narrative']: 'Storytelling & Narrative',
      ['educational-informative']: 'Educational & Informative',
      ['other']: 'Other',
    };
    return toneMap[tone] || 'Other';
  }
}
